import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import * as cpProgramActions from '../actions/cpProgramActions';
import { CPProgramState } from '../models/cpProgram';
import { cpProgramDefaultState } from '../models/cpProgramData';
import * as weekFourActions from '../weekFour/actions/weekFourActions';
import { OptionsQuestion, SelectedAnswer } from '../weekFour/models/weekFour';
import * as weekOneActions from '../weekOne/actions/weekOneActions';
import * as weekSharedActions from '../weekShared/actions/weekSharedActions';
import * as weekThreeActions from '../weekThree/actions/weekThreeActions';
import * as weekTwoActions from '../weekTwo/actions/weekTwoActions';

const changeAnswer = (field: string, value: string, question: OptionsQuestion) => ({
    ...question,
    answers: question.answers.map(answer => {
        if (field === 'answer') {
            return {
                ...answer,
                isChosen: answer.description === value,
            };
        }

        return answer.description === 'Other'
            ? {
                  ...answer,
                  [field]: value,
              }
            : answer;
    }),
});

const updateAnswer = (givenAnswer: SelectedAnswer, question: OptionsQuestion) => ({
    ...question,
    answers: question.answers.map(answer =>
        answer.description === givenAnswer.description
            ? {
                  ...answer,
                  isChosen: true,
                  additionalAnswer: givenAnswer.additionalAnswer,
              }
            : answer,
    ),
});

export const cpProgram = (
    state: CPProgramState = cpProgramDefaultState,
    action: cpProgramActions.CPProgramAction,
) => {
    switch (action.type) {
        case weekSharedActions.FETCH_CORE_PILLARS_RATING_SUCCEEDED: {
            const rating = action.payload;

            return {
                ...state,
                corePillarsRating: state.weeks.reduce((acc, week) => {
                    return {
                        ...acc,
                        [week.id]: rating[week.title] || 0,
                    };
                }, {}),
            };
        }
        case weekSharedActions.CHANGE_CORE_PILLAR_RATING: {
            const { weekId, value } = action.payload;

            return {
                ...state,
                corePillarsRating: {
                    ...state.corePillarsRating,
                    [weekId]: value,
                },
            };
        }
        case weekOneActions.CHANGE_MINDFULNESS_IMPROVEMENTS_TOPIC: {
            const { field, value } = action.payload;
            const topics: string[] = state.mindfulnessImprovementsTopics[field];
            const updatedTopics = topics.includes(value)
                ? topics.filter(topic => topic != value)
                : [...topics, value];

            return {
                ...state,
                mindfulnessImprovementsTopics: {
                    ...state.mindfulnessImprovementsTopics,
                    [field]: updatedTopics,
                },
            };
        }
        case weekOneActions.CHANGE_MINDFULNESS_IMPROVEMENTS_ADDITIONAL_INFO:
            return {
                ...state,
                mindfulnessImprovementsTopics: {
                    ...state.mindfulnessImprovementsTopics,
                    [action.payload.field]: action.payload.value,
                },
            };
        case weekOneActions.FETCH_MINDFULNESS_IMPROVEMENTS_DATA_SUCCEEDED: {
            const mindfulnessImprovementsTopics = action.payload;

            return !isEmpty(mindfulnessImprovementsTopics)
                ? {
                      ...state,
                      mindfulnessImprovementsTopics,
                  }
                : state;
        }
        case weekOneActions.SET_MINDFULNESS_EXERCISE_COMPLETED: {
            return {
                ...state,
                isMindfulnessExerciseCompleted: true,
            };
        }
        case weekTwoActions.CHANGE_HOMEWORK_CONFIDENCE_RATING_VALUE:
            return {
                ...state,
                homeworkConfidenceRating: action.payload,
            };
        case weekTwoActions.CHANGE_VALUES_ASSESSMENT_AREA_FIELD: {
            const { domainTitle, value, field } = action.payload;

            return {
                ...state,
                valuesAssessments: state.valuesAssessments.map(assessmentDomain => {
                    if (assessmentDomain.title !== domainTitle) {
                        return assessmentDomain;
                    }

                    if (field !== 'topic') {
                        return {
                            ...assessmentDomain,
                            [field]: value,
                        };
                    }

                    const topics: string[] = assessmentDomain.topics;
                    const updatedTopics = topics.includes(value as string)
                        ? topics.filter(topic => topic != value)
                        : [...topics, value];

                    return {
                        ...assessmentDomain,
                        topics: updatedTopics,
                    };
                }),
            };
        }
        case weekTwoActions.FETCH_VALUES_ASSESSMENTS_DATA_SUCCEEDED: {
            const valuesAssessments = action.payload;
            return !isEmpty(valuesAssessments)
                ? {
                      ...state,
                      valuesAssessments,
                  }
                : state;
        }

        case weekTwoActions.RESET_AREA_ADDITIONAL_RATE: {
            return {
                ...state,
                valuesAssessments: state.valuesAssessments.map(domain => ({
                    ...domain,
                    additionalRate: 0,
                })),
            };
        }
        case weekThreeActions.CHANGE_LIVING_ACCORDING_TO_VALUES_FEELINGS:
            return {
                ...state,
                livingAccordingToValues: {
                    ...state.livingAccordingToValues,
                    [action.payload.field]: [action.payload.value],
                },
            };

        case weekThreeActions.CHANGE_ACTION_ACTIVITIES: {
            const { domainTitle, description } = action.payload;

            return {
                ...state,
                activitiesSelection: state.activitiesSelection.map(domain => {
                    if (domain.title !== domainTitle) {
                        return domain;
                    }

                    return {
                        ...domain,
                        activities: domain.activities.map(activity =>
                            activity.description === description
                                ? activity.isChosen
                                    ? {
                                          ...activity,
                                          isChosen: false,
                                      }
                                    : {
                                          ...activity,
                                          dateToBePerformed: moment().startOf('h'),
                                          isChosen: true,
                                      }
                                : {
                                      ...activity,
                                  },
                        ),
                    };
                }),
            };
        }
        case weekThreeActions.CHANGE_ACTIVITY_DATA: {
            const { domainTitle, description, field, value } = action.payload;

            return {
                ...state,
                activitiesSelection: state.activitiesSelection.map(domain => {
                    if (domain.title !== domainTitle) {
                        return domain;
                    }

                    return {
                        ...domain,
                        activities: domain.activities.map(activity =>
                            activity.description === description
                                ? {
                                      ...activity,
                                      description,
                                      dateToBePerformed: moment().startOf('h'),
                                      [field]: value,
                                      isChosen: true,
                                  }
                                : activity,
                        ),
                    };
                }),
            };
        }

        case weekThreeActions.FETCH_PLANNED_ACTIVITIES_SUCCEEDED: {
            return {
                ...state,
                activitiesSelection: state.activitiesSelection.map(selectedDomain => {
                    const selectedDomainFetchedActivities = action.payload.find(
                        activity => activity.domain === selectedDomain.id,
                    );

                    if (!selectedDomainFetchedActivities) {
                        return selectedDomain;
                    }

                    return {
                        ...selectedDomain,
                        activities: selectedDomain.activities.map(selectedDomainActivity => {
                            const plannedActivity = action.payload.find(
                                activity =>
                                    activity.description === selectedDomainActivity.description,
                            );

                            return plannedActivity && plannedActivity.domain === selectedDomain.id
                                ? {
                                      ...selectedDomainActivity,
                                      isChosen: true,
                                      dateToBePerformed: moment(plannedActivity.dateToBePerformed),
                                      id: plannedActivity.id,
                                  }
                                : selectedDomainActivity;
                        }),
                    };
                }),
            };
        }

        case weekFourActions.CHANGE_FLEXIBILITY_EXERCISE_ANSWER: {
            const { id, value, field } = action.payload;

            return {
                ...state,
                flexibilityExerciseQuestions: state.flexibilityExerciseQuestions.map(question => {
                    if (question.id !== id) {
                        return question;
                    }
                    return changeAnswer(field, value, question);
                }),
            };
        }

        case weekFourActions.FETCH_FLEXIBILITY_EXERCISE_QUESTIONS_SUCCEEDED: {
            const flexibilityExerciseQuestions = action.payload;

            return !isEmpty(flexibilityExerciseQuestions)
                ? {
                      ...state,
                      flexibilityExerciseQuestions,
                  }
                : state;
        }

        case weekFourActions.FETCH_FLEXIBILITY_EXERCISE_ANSWERS_SUCCEEDED: {
            return {
                ...state,
                flexibilityExerciseQuestions: state.flexibilityExerciseQuestions.map(question => {
                    const givenAnswer = action.payload.find(
                        fetchedQuestion => fetchedQuestion.id === question.id,
                    );

                    if (!givenAnswer) {
                        return question;
                    }

                    return updateAnswer(givenAnswer, question);
                }),
            };
        }

        case weekFourActions.FETCH_FLEXIBILITY_PRACTICE_QUESTION_SUCCEEDED:
            return {
                ...state,
                flexibilityPracticeQuestion: action.payload,
            };

        case weekFourActions.FETCH_FLEXIBILITY_PRACTICE_ANSWER_SUCCEEDED:
            return {
                ...state,
                flexibilityPracticeQuestion: {
                    ...state.flexibilityPracticeQuestion,
                    answer: action.payload,
                },
            };

        case weekFourActions.CHANGE_FLEXIBILITY_PRACTICE_ANSWER: {
            return {
                ...state,
                flexibilityPracticeQuestion: {
                    ...state.flexibilityPracticeQuestion,
                    answer: action.payload.value,
                },
            };
        }

        case weekFourActions.FETCH_FLEXIBILITY_PLAN_DATA_SUCCEEDED:
            return {
                ...state,
                flexibilityPlan: action.payload,
            };

        case weekFourActions.CHANGE_FLEXIBILITY_PLAN_FIELD: {
            const { id, field, value } = action.payload;

            return {
                ...state,
                flexibilityPlan: state.flexibilityPlan.map(question => {
                    if (question.id !== id) {
                        return question;
                    }

                    return changeAnswer(field, value, question);
                }),
            };
        }

        case weekFourActions.CHANGE_FLEXIBILITY_PLAN_INPUT: {
            const { id, value } = action.payload;

            return {
                ...state,
                flexibilityPlan: state.flexibilityPlan.map(question => {
                    if (question.id !== id) {
                        return question;
                    }
                    return {
                        ...question,
                        answer: value,
                    };
                }),
            };
        }

        case weekFourActions.FETCH_FLEXIBILITY_PLAN_ANSWERS_SUCCEEDED: {
            return {
                ...state,
                flexibilityPlan: state.flexibilityPlan.map(question => {
                    const givenAnswer = action.payload.find(
                        fetchedQuestion => fetchedQuestion.id === question.id,
                    );

                    if (!givenAnswer) {
                        return question;
                    }

                    return givenAnswer.description
                        ? updateAnswer(givenAnswer, question)
                        : {
                              ...question,
                              answer: givenAnswer.answer,
                          };
                }),
            };
        }
        case weekFourActions.CHANGE_ACCOMPLISHED_ACTIVITY_FIELD: {
            const { activityTitle, areaTitle, value, field } = action.payload;

            return {
                ...state,
                activitiesSelection: state.activitiesSelection.map(area => {
                    if (area.title !== areaTitle) {
                        return area;
                    }

                    return {
                        ...area,
                        activities: area.activities.map(activity =>
                            activity.description === activityTitle
                                ? {
                                      ...activity,
                                      [field]: value,
                                  }
                                : activity,
                        ),
                    };
                }),
            };
        }
        case weekThreeActions.FETCH_ACTIVITIES_DATA_SUCCEEDED: {
            return {
                ...state,
                activitiesSelection: action.payload,
            };
        }

        default:
            return state;
    }
};
